/**
 * API del módulo Contabilidad — espejo de `backend/app/schemas/contabilidad.py`.
 */
import { ApiError, apiDownload, apiRequest } from "../../lib/api";
import { clearAuth, getAuth } from "../../lib/auth";

export interface Periodo {
  anio: number | null;
  desde: string;
  hasta: string;
  vacio: boolean;
  publicado_hasta: string | null;
  mostrar_provisorio: boolean;
  ultimo_dato: string | null;
  provisorio_desde: string | null;
  anios_disponibles: number[];
}

export interface FiltroPeriodo {
  anio?: number | undefined;
  desde?: string | undefined;
  hasta?: string | undefined;
}

export interface CuentaMonto {
  cuenta_id: number;
  codigo: string;
  nombre: string;
  monto: number;
}

export interface MesResumen {
  anio: number;
  mes: number;
  etiqueta: string;
  provisorio: boolean;
  ingresos: number;
  gastos: number;
  resultado: number;
  disponible: number;
}

export interface Resumen {
  periodo: Periodo;
  disponible: number;
  disponible_detalle: CuentaMonto[];
  resultado_mes: number;
  resultado_acumulado: number;
  ingresos_acumulados: number;
  gastos_acumulados: number;
  deudores_socios: number;
  obligaciones: number;
  meses: MesResumen[];
  gastos_por_rubro: { rubro: string; titulo: string; total: number; cuentas: CuentaMonto[] }[];
  cuentas_sin_clasificar: number;
}

export interface FilaBalance {
  codigo: string;
  nombre: string;
  cuenta_id: number | null;
  debitos: number;
  creditos: number;
  saldo_deudor: number;
  saldo_acreedor: number;
  activo: number;
  pasivo: number;
  perdida: number;
  ganancia: number;
}

export interface Balance {
  periodo: Periodo;
  nivel: number;
  filas: FilaBalance[];
  sumas: FilaBalance;
  resultado: FilaBalance;
  totales: FilaBalance;
  utilidad: number;
  cuadra: boolean;
}

export interface Cuenta {
  id: number;
  codigo: string;
  nombre: string;
  tipo: TipoCuenta | null;
  rubro: string | null;
  revisada: boolean;
}

export type TipoCuenta = "activo" | "pasivo" | "patrimonio" | "ingreso" | "gasto";

export interface Rubro {
  codigo: string;
  tipo: TipoCuenta;
  titulo: string;
}

export interface Mayor {
  periodo: Periodo;
  cuenta: Cuenta;
  deudora: boolean;
  saldo_inicial: number;
  lineas: {
    comprobante_id: number;
    fecha: string;
    comprobante: string;
    glosa: string;
    documento: string | null;
    debe: number;
    haber: number;
    saldo: number;
  }[];
  total_debe: number;
  total_haber: number;
  saldo_final: number;
}

export interface ComprobanteResumen {
  id: number;
  fecha: string;
  tipo: string;
  numero: number;
  glosa: string;
  total: number;
  provisorio: boolean;
}

export interface ComprobanteDetalle {
  id: number;
  fecha: string;
  tipo: string;
  numero: number;
  glosa: string;
  provisorio: boolean;
  lineas: {
    cuenta_id: number;
    codigo: string;
    nombre: string;
    debe: number;
    haber: number;
    glosa: string;
    documento: string | null;
  }[];
}

export interface Ejercicio {
  id: number;
  anio: number;
  publicado_hasta: string | null;
  mostrar_provisorio: boolean;
  ultimo_dato: string | null;
}

export interface Carga {
  id: number;
  archivo_nombre: string;
  usuario_nombre: string | null;
  fecha_desde: string;
  fecha_hasta: string;
  estado: "vigente" | "reemplazada";
  created_at: string;
  anio: number;
  comprobantes: number;
  lineas: number;
  agregados: number;
  quitados: number;
}

export interface ResultadoCarga {
  confirmado: boolean;
  carga_id: number | null;
  resumen: {
    anio: number;
    fecha_desde: string;
    fecha_hasta: string;
    comprobantes: number;
    lineas: number;
    total_debe: number;
    por_tipo: Record<string, number>;
    cuentas_nuevas: { codigo: string; nombre: string; tipo: string | null; rubro: string | null }[];
    agregados: number;
    quitados: number;
    hay_carga_anterior: boolean;
    sin_cambios: boolean;
  } | null;
  errores: { fila: number | null; mensaje: string }[];
}

const BASE = "/api/v1/contabilidad";

function query(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const fetchResumen = (f: FiltroPeriodo): Promise<Resumen> =>
  apiRequest(`${BASE}/resumen${query({ ...f })}`);

export const fetchBalance = (f: FiltroPeriodo, nivel: number): Promise<Balance> =>
  apiRequest(`${BASE}/balance-8-columnas${query({ ...f, nivel })}`);

export const fetchMayor = (cuentaId: number, f: FiltroPeriodo): Promise<Mayor> =>
  apiRequest(`${BASE}/mayor/${cuentaId}${query({ ...f })}`);

export const fetchComprobantes = (
  f: FiltroPeriodo & {
    tipo?: string | undefined;
    q?: string | undefined;
    monto?: number | undefined;
    limit: number;
    offset: number;
  },
): Promise<{ periodo: Periodo; items: ComprobanteResumen[]; total: number }> =>
  apiRequest(`${BASE}/comprobantes${query({ ...f })}`);

export const fetchComprobante = (id: number): Promise<ComprobanteDetalle> =>
  apiRequest(`${BASE}/comprobantes/${id}`);

export const fetchCuentas = (): Promise<Cuenta[]> => apiRequest(`${BASE}/cuentas`);

/** Nivel superior del plan (`1`, `1-01`…); `nombre` null = el balance muestra el código. */
export interface GrupoCuenta {
  codigo: string;
  nivel: number;
  nombre: string | null;
  cuentas: number;
}

export const fetchGrupos = (): Promise<GrupoCuenta[]> => apiRequest(`${BASE}/grupos`);

export const nombrarGrupo = (codigo: string, nombre: string | null): Promise<GrupoCuenta> =>
  apiRequest(`${BASE}/grupos/${encodeURIComponent(codigo)}`, { method: "PUT", body: { nombre } });
export const fetchRubros = (): Promise<Rubro[]> => apiRequest(`${BASE}/rubros`);
export const fetchEjercicios = (): Promise<Ejercicio[]> => apiRequest(`${BASE}/ejercicios`);
export const fetchCargas = (): Promise<Carga[]> => apiRequest(`${BASE}/cargas`);

export const actualizarCuenta = (
  id: number,
  body: { tipo?: TipoCuenta | null; rubro?: string | null; revisada?: boolean },
): Promise<Cuenta> => apiRequest(`${BASE}/cuentas/${id}`, { method: "PATCH", body });

export const publicar = (
  anio: number,
  body: { publicado_hasta: string | null; mostrar_provisorio: boolean },
): Promise<Ejercicio> =>
  apiRequest(`${BASE}/ejercicios/${anio}/publicacion`, { method: "PUT", body });

/** Descarga un informe. `recurso`: balance-8-columnas | mayor | comprobantes. */
export function descargar(
  recurso: "balance-8-columnas" | "mayor" | "comprobantes",
  formato: "xlsx" | "pdf",
  params: FiltroPeriodo & { nivel?: number | undefined; cuenta_id?: number | undefined },
): Promise<void> {
  return apiDownload(`${BASE}/${recurso}/export.${formato}${query({ ...params })}`, `${recurso}.${formato}`);
}

/** Sube el libro diario (multipart). `confirmar=false` = vista previa. */
export async function subirLibroDiario(archivo: File, confirmar: boolean): Promise<ResultadoCarga> {
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
  const form = new FormData();
  form.append("file", archivo);
  const auth = getAuth();
  const response = await fetch(`${base}${BASE}/cargas?confirmar=${confirmar}`, {
    method: "POST",
    headers: auth ? { Authorization: `Bearer ${auth.accessToken}` } : {},
    body: form,
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      clearAuth();
      window.location.replace("/login");
    }
    const err = (payload as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(response.status, err?.code ?? "http_error", err?.message ?? `HTTP ${response.status}`);
  }
  return payload as ResultadoCarga;
}

export interface Matriz {
  periodo: Periodo;
  columnas: { anio: number; mes: number; etiqueta: string; provisorio: boolean }[];
  filas: {
    cuenta_id: number;
    codigo: string;
    nombre: string;
    tipo: TipoCuenta | null;
    rubro: string | null;
    /** Naturaleza opuesta a la de su tipo (depreciación acumulada): resta en el total del tipo. */
    contra: boolean;
    meses: number[];
  }[];
}

export const fetchMatriz = (f: FiltroPeriodo): Promise<Matriz> =>
  apiRequest(`${BASE}/matriz${query({ anio: f.anio, hasta: f.hasta })}`);

export function descargarMatriz(f: FiltroPeriodo, modo: "movimiento" | "saldo"): Promise<void> {
  return apiDownload(
    `${BASE}/matriz/export.xlsx${query({ anio: f.anio, hasta: f.hasta, modo })}`,
    "cuentas_por_mes.xlsx",
  );
}
